import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import CategoryBadge from "@/components/base/CategoryBadge";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useFavorites } from "@/hooks/useFavorites";
import {
  findCharacter,
  findDrama,
  findNews,
  formatDate,
  getRelated,
} from "@/utils/content";
import { CATEGORY_LABEL, CATEGORY_PATH, type ContentCategory } from "@/types/content";

interface DetailView {
  id: string;
  category: ContentCategory;
  title: string;
  cover: string;
  summary: string;
  body: string[];
  tags: string[];
  meta: { label: string; value: string }[];
}

function resolveDetail(id: string): DetailView | null {
  const drama = findDrama(id);
  if (drama) {
    return {
      id: drama.id,
      category: "drama",
      title: drama.title,
      cover: drama.cover,
      summary: drama.summary,
      body: drama.content,
      tags: drama.tags,
      meta: [
        { label: "发布时间", value: formatDate(drama.date) },
        { label: "作者", value: drama.author },
        { label: "年份", value: drama.year },
        { label: "阅读时长", value: drama.readTime },
      ],
    };
  }

  const character = findCharacter(id);
  if (character) {
    return {
      id: character.id,
      category: "characters",
      title: character.name,
      cover: character.avatar,
      summary: character.summary,
      body: character.background,
      tags: character.traits,
      meta: [
        { label: "角色定位", value: character.role },
        { label: "饰演演员", value: character.actor },
        { label: "身份", value: character.position },
      ],
    };
  }

  const newsItem = findNews(id);
  if (newsItem) {
    return {
      id: newsItem.id,
      category: "news",
      title: newsItem.title,
      cover: newsItem.cover,
      summary: newsItem.summary,
      body: newsItem.content,
      tags: newsItem.tags,
      meta: [
        { label: "发布日期", value: formatDate(newsItem.date) },
        { label: "作者", value: newsItem.author },
        { label: "来源", value: newsItem.source },
        { label: "热度", value: String(newsItem.hot) },
      ],
    };
  }

  return null;
}

export default function Detail() {
  const { id = "" } = useParams();
  const detail = resolveDetail(id);
  const [toast, setToast] = useState("");
  const { isFavorite, toggle } = useFavorites();

  useDocumentTitle({
    title: detail ? `${detail.title} - 我妻子的一切` : "内容不存在 - 我妻子的一切",
    description: detail ? detail.summary.slice(0, 140) : "抱歉，你访问的内容不存在或已失效。",
    keywords: detail ? `${detail.title},我妻子的一切,${CATEGORY_LABEL[detail.category]}` : undefined,
  });

  useEffect(() => {
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!detail) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容不存在或加载失败"
          description="抱歉，你访问的内容可能已被移除，或者链接不正确。返回首页看看其他内容吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-800"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(detail.id);
  const related = getRelated(detail.category, detail.id, 5);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制（演示功能）");
    } catch {
      setToast("链接复制失败，请手动复制地址栏（演示功能）");
    }
  };

  return (
    <div className="w-full px-4 py-8 pb-24 md:px-8 md:py-12 md:pb-12">
      <Breadcrumb
        items={[
          { label: CATEGORY_LABEL[detail.category], to: CATEGORY_PATH[detail.category] },
          { label: detail.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryBadge category={detail.category} />
            {detail.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-accent-700">
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {detail.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
            {detail.meta.map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1.5">
                <span className="text-foreground-400">{item.label}：</span>
                {item.value}
              </span>
            ))}
          </div>

          <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={detail.cover}
              alt={detail.title}
              title={`${detail.title} - 我妻子的一切`}
              className="h-64 w-full object-cover object-top md:h-[420px]"
            />
          </div>

          <p className="mt-6 border-l-2 border-accent-500 bg-background-100 px-4 py-3 text-sm leading-7 text-foreground-700">
            {detail.summary}
          </p>

          <div className="mt-6 flex flex-col gap-5">
            {detail.body.map((paragraph) => (
              <p key={paragraph} className="text-base leading-8 text-foreground-800">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <button
              type="button"
              onClick={() => {
                toggle(detail.id);
                setToast(favorite ? "已取消收藏（本地演示）" : "已加入收藏（本地演示）");
              }}
              className={[
                "inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer",
                favorite
                  ? "bg-accent-500 text-secondary-950"
                  : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-700",
              ].join(" ")}
            >
              <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
              {favorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-forward-line" />
              分享链接
            </button>
            <Link
              to={CATEGORY_PATH[detail.category]}
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
            >
              <i className="ri-folder-line" />
              查看所属分类
            </Link>
            <span className="text-xs text-foreground-400">收藏为浏览器本地存储演示，不与账号绑定</span>
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关阅读
              </h2>
              <ul className="mt-4 flex flex-col divide-y divide-background-100">
                {related.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex items-start gap-3 py-3 first:pt-0"
                    >
                      <span className="mt-0.5 font-heading text-lg font-bold text-background-400">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 text-sm text-foreground-700 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 block">
                          <CategoryBadge category={item.category} />
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg bg-primary-900 p-5">
              <h2 className="font-heading text-sm font-semibold text-background-50">免责声明</h2>
              <p className="mt-2 text-xs leading-6 text-background-400">
                本站为影视内容演示站点，所有文章与数据均为示例内容，不代表任何真实官方发布信息。
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* mobile detail action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-background-300/70 bg-background-50/95 px-4 py-2 backdrop-blur-md md:hidden">
        <button
          type="button"
          onClick={() => {
            toggle(detail.id);
            setToast(favorite ? "已取消收藏（本地演示）" : "已加入收藏（本地演示）");
          }}
          className={[
            "flex flex-col items-center gap-0.5 px-5 py-1 text-[11px] cursor-pointer",
            favorite ? "text-accent-700" : "text-foreground-600",
          ].join(" ")}
        >
          <i className={favorite ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"} />
          {favorite ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="flex flex-col items-center gap-0.5 px-5 py-1 text-[11px] text-foreground-600 cursor-pointer"
        >
          <i className="ri-share-forward-line text-lg" />
          分享
        </button>
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex flex-col items-center gap-0.5 px-5 py-1 text-[11px] text-foreground-600 cursor-pointer"
        >
          <i className="ri-arrow-up-double-line text-lg" />
          返回顶部
        </button>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 md:bottom-10">
          {toast}
        </div>
      )}
    </div>
  );
}